import { Box, Button, Flex, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { ChevronDown, X } from 'lucide-react';
import { useMemo } from 'react';
import { t } from '@lingui/core/macro';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { formatTaskType } from '../SupportTasks/utils/formatters';
import { RULE_STATUS_KEYS, type RuleStatusKey, type RuleTaskKind } from './ruleDisplay';

type FilterOption<T extends string> = { value: T; label: string };

function FilterMenu<T extends string>({
  value,
  placeholder,
  clearLabel,
  options,
  onChange,
}: {
  value?: T;
  placeholder: string;
  clearLabel: string;
  options: Array<FilterOption<T>>;
  onChange: (value?: T) => void;
}) {
  const label = options.find((option) => option.value === value)?.label ?? placeholder;

  // Same shape as the Support Tasks filters and the shared
  // MultipleChoiceFilter: the clear control goes in rightIcon, which Chakra
  // renders outside MenuButton's `pointer-events: none` children wrapper and
  // so stays clickable. flexShrink keeps the button at its content width
  // beside the search box.
  return (
    <Menu isLazy>
      <MenuButton
        as={Button}
        variant="outline"
        size="sm"
        px={2}
        width="auto"
        colorScheme="gray"
        flexShrink={0}
        rightIcon={
          value != null ? (
            <Box
              aria-label={clearLabel}
              ml={1}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(undefined);
              }}
            >
              <X size={iconSize.md} />
            </Box>
          ) : (
            <ChevronDown size={iconSize.md} />
          )
        }
      >
        {label}
      </MenuButton>
      <MenuList>
        {/* The X in rightIcon sits inside MenuButton, so it cannot be made
            focusable without nesting a control in a button. This item is the
            keyboard route to the same action. */}
        {value != null && <MenuItem onClick={() => onChange(undefined)}>{clearLabel}</MenuItem>}
        {options.map((option) => (
          <MenuItem key={option.value} onClick={() => onChange(option.value)}>
            {option.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

export function AutomationRuleFilters({
  kind,
  status,
  availableKinds,
  onKindChange,
  onStatusChange,
}: {
  kind?: RuleTaskKind;
  status?: RuleStatusKey;
  // Only the kinds the seeded rules actually use, so the menu never offers an
  // option that matches nothing.
  availableKinds: RuleTaskKind[];
  onKindChange: (value?: RuleTaskKind) => void;
  onStatusChange: (value?: RuleStatusKey) => void;
}) {
  // Several kinds can share one label — the NYB and day-35 withdrawal warning
  // EC emails both read as "Withdrawal Warning EC" — so the menu is deduped by
  // label, not by kind. The page filters on the label for the same reason.
  const kindOptions = useMemo(() => {
    const byLabel = new Map<string, RuleTaskKind>();
    for (const value of availableKinds) {
      const label = formatTaskType(value, null);
      if (!byLabel.has(label)) {
        byLabel.set(label, value);
      }
    }
    return [...byLabel]
      .map(([label, value]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableKinds]);

  const statusOptions = useMemo((): Array<FilterOption<RuleStatusKey>> => {
    const labels: Record<RuleStatusKey, string> = {
      enabled: t`Enabled`,
      disabled: t`Disabled`,
      invalid: t`Invalid`,
    };
    return RULE_STATUS_KEYS.map((value) => ({ value, label: labels[value] }));
  }, []);

  return (
    <Flex gap={2}>
      {/* Syncs itself to the `search` param, which the page reads back. */}
      <SearchInput maxW="search-input" placeholder={t`Search rules by name`} />
      <FilterMenu
        value={kind}
        placeholder={t`Type`}
        clearLabel={t`Clear type filter`}
        options={kindOptions}
        onChange={onKindChange}
      />
      <FilterMenu
        value={status}
        placeholder={t`Status`}
        clearLabel={t`Clear status filter`}
        options={statusOptions}
        onChange={onStatusChange}
      />
    </Flex>
  );
}
